import { Snackbar } from '@mui/material'
import { GenerateBS } from 'components/GenerateBS'
import html2pdf from 'html2pdf.js'
import moment from 'moment'
import React, { useEffect, useRef, useState } from 'react'
import { Alert } from 'react-bootstrap'
import 'react-datepicker/dist/react-datepicker.css'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import Select from 'react-select'
import { FinancialReportItem } from 'store/settings/types'
import ViewRegisterModal from '../../components/Modal/ProfitLoss'
import ReportRefreshBanner, {
  showRefreshCompletedToast,
} from '../../components/ReportRefreshBanner'
import SAlert from '../../components/SAlert'
import ScrollToggle from '../../components/ScrollToggle/ScrollToggle'
import { MONTHS, URLS } from '../../resources/constants'
import { lastYears, usdCurrencyFormat } from '../../resources/functions'
import { calculateGroupedTotals } from '../../resources/helper'
import { useReportApi } from '../../resources/hooks/api/useReportApiHook'
import { useSettingsApi } from '../../resources/hooks/api/useSettingsApiHook'
import {
  getAccountSettings,
  getBsRegenerationFailed,
  getBsRegenerationFailedMonth,
  getBsRegenerationFailedMsg,
  getIsQbdImport,
} from '../../store/settings/selectors'
import { getBranch } from '../../store/user/selectors'


const BalanceSheet: React.FC = () => {
  const { balanceSheetData, balanceSheetRefresh, regenerateBalanceSheet, downloadReport } =
    useReportApi()
  const { fetchAccountSettingsData } = useSettingsApi()
  const contentRef = useRef<HTMLDivElement >(null)
  const previousRefreshInProgressRef = useRef(false)
  const { t } = useTranslation(['scenes.balanceSheet','resources.form-validator'])

  const [isNotify, setIsNotify] = useState<boolean>(false)
  const [isBalanced, setIsBalanced] = useState<boolean>(true)
  const [balanceDifference, setBalanceDifference] = useState<number>(0)
  const [firstImbalancedMonth, setFirstImbalancedMonth] = useState<number | null>(null)
  const [firstImbalancedYear, setFirstImbalancedYear] = useState<number | null>(null)
  const [unreconciledPeriods, setUnreconciledPeriods] = useState<any[]>([])
  // True when a user-initiated Regenerate completed but the sheet is STILL imbalanced
  // (i.e. a data issue regeneration can't fix). regenVerifyPendingRef marks that the
  // next balance-sheet fetch follows such a Regenerate and should be evaluated.
  const [dataIssueAfterRegen, setDataIssueAfterRegen] = useState<boolean>(false)
  const regenVerifyPendingRef = useRef<boolean>(false)
  const [glBalanceSheetList, setGlBalanceSheetList] = useState<FinancialReportItem[]>([])
  const [searchParams, setSearchParams] = useState<any>({
    month: { value: '01', label: t('month.01', { ns: 'resources.form-validator' }) },
    year: {
      value: new Date().getFullYear(),
      label: '' + new Date().getFullYear(),
    },
  })
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [regenerationPending, setRegenerationPending] = useState<any>(null)
  const [isDownloading, setIsDownloading] = useState<boolean>(false)
  const [showConfirm, setShowConfirm] = useState<boolean>(false)
  const [isThirdPartyEnabled, setIsThirdPartyEnabled] = useState<boolean>(false)
  const [isShowViewRegister, setIsShowViewRegister] = useState<boolean>(false)
  const [companyInfo, setCompanyInfo] = useState<any>(null)
  const [txnRegisterPayload, setTxnRegisterPayload] = useState<{
    startDate: string
    endDate: string
    account: number
    hasEditAccess: boolean
  }>({
    startDate: moment().format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
    account: 0,
    hasEditAccess: false,
  })
      const currentDate = moment(
        `${searchParams.year.value}-${searchParams.month.value}-01`
      )
        .endOf('month')
        .format('MM/DD/YYYY')

      const priorDate = moment(
        `${searchParams.year.value - 1}-${searchParams.month.value}-01`
      )
        .endOf('month')
        .format('MM/DD/YYYY')
  const branchData = useSelector(getBranch)
  const accountSettings = useSelector(getAccountSettings)
  const branchId = +branchData['id']
  const isRefreshInProgress = !!accountSettings.bs_refresh_in_progress
  // The refresh runs in the background: show only the progress banner, never a
  // blocking overlay, so the user keeps full use of the page while it works.
  const showRefreshBanner = isRefreshInProgress
 
  const isQbdImport = useSelector(getIsQbdImport)
  const bsRegenerationFailed = useSelector(getBsRegenerationFailed)
  const bsRegenerationFailedMonth = useSelector(getBsRegenerationFailedMonth)
  const bsRegenerationFailedMsg = useSelector(getBsRegenerationFailedMsg)
  const showRegenerationFailedBanner =
    isQbdImport && bsRegenerationFailed && !showRefreshBanner
 const tableContainerRef = useRef<HTMLDivElement>(null)
  const fetchBalanceSheetList = () => {
    const params: any = {
      page: 1,
      per_page: 5000,
      year: searchParams.year['value'],
      month: searchParams.month['value'],
      branch_id: +branchData['id'],
    }

    setIsLoading(true)
    balanceSheetData(
      params,
      (message: string, resp: any) => {
      
        if (resp?.data?.data?.regeneration_pending) {
          setGlBalanceSheetList([])
          setRegenerationPending(resp.data.data.regeneration_pending)
          setIsLoading(false)
          getSettingsData()
          return
        }
        setRegenerationPending(null)
        if (
          resp &&
          resp['data'] &&
          resp['data']['data'] &&
          resp['data']['data']['result']
        ) {
          const companyInfo = resp.data.data?.company_details ?? null
          const calculatedData = calculateGroupedTotals(resp.data.data.result)
          const isThirdPartyEnabled =
            resp.data.data?.is_third_party_enable ?? false

          setCompanyInfo(companyInfo)
          setGlBalanceSheetList(calculatedData)
          // setGlBalanceSheetList(resp['data']['data']['result'])
          // if (isThirdPartyEnabled) {
          //     setGlBalanceSheetList(resp['data']['data']['result'])
          // } else {
          //     setGlBalanceSheetList(calculatedData)
          // }
          setIsNotify(resp.data.data?.is_notify || false)
          const balanced = resp.data.data?.is_balanced !== false
          setIsBalanced(balanced)
          setBalanceDifference(resp.data.data?.balance_difference || 0)
          setFirstImbalancedMonth(resp.data.data?.first_imbalanced_month || null)
          setFirstImbalancedYear(resp.data.data?.first_imbalanced_year || null)
          setUnreconciledPeriods(resp.data.data?.unreconciled_periods || [])
          // If this fetch follows a user-initiated Regenerate, a still-imbalanced
          // sheet means regeneration couldn't fix it — i.e. a data issue.
          if (regenVerifyPendingRef.current) {
            setDataIssueAfterRegen(!balanced)
            regenVerifyPendingRef.current = false
          } else if (balanced) {
            setDataIssueAfterRegen(false)
          }
          setIsLoading(false)

          setIsNotify(resp.data.data?.is_notify || false)
          setIsThirdPartyEnabled(isThirdPartyEnabled)
        } else {
          setGlBalanceSheetList([])
          setIsLoading(false)
        }
      },
      (message: string, resp: any) => {
        setGlBalanceSheetList([])
        setIsLoading(false)
        toast.error(message)
      }
    )
  }

  const handleRegenerate = () => {
    setShowConfirm(false)
    // The completion re-fetch should evaluate whether the sheet is still imbalanced.
    regenVerifyPendingRef.current = true
    setDataIssueAfterRegen(false)
    const params: any = {
      year: searchParams.year['value'],
      month: searchParams.month['value'],
      branch_id: +branchData['id'],
    }

    setIsLoading(true)
    regenerateBalanceSheet(
      params,
      (message: string, resp: any) => {
        setIsLoading(false)
        const data = resp?.data?.data
        if (data?.status === 'started') {
          // Regeneration cascade runs in the background. Pick up the in-progress
          // flag so the refresh banner + polling take over; the completion effect
          // re-fetches the balance sheet when the run finishes.
          toast.success(resp?.data?.message || t('alerts.regenStarted'))
          getSettingsData()
        } else {
          // Nothing was queued (status 'noop') — just refresh the current view.
          toast.success(resp?.data?.message || t('alerts.regenNoop'))
          fetchBalanceSheetList()
        }
      },
      (message: string, resp: any) => {
        setIsLoading(false)
         toast.error(message || t('alerts.regenFailed'))
      }
    )
  }


  const handleRetryFailedRegeneration = () => {
    if (!bsRegenerationFailedMonth) return
    const [failedYear, failedMonth] = bsRegenerationFailedMonth.split('-').map(Number)
    if (!failedYear || !failedMonth) return

    regenVerifyPendingRef.current = true
    setDataIssueAfterRegen(false)
    setIsLoading(true)
    regenerateBalanceSheet(
      { year: failedYear, month: failedMonth, branch_id: +branchData['id'] },
      (message: string, resp: any) => {
        setIsLoading(false)
        toast.success(resp?.data?.message || t('alerts.regenStarted'))
        getSettingsData()
      },
      (message: string, resp: any) => {
        setIsLoading(false)
        toast.error(message || t('alerts.regenFailed'))
      }
    )
  }

  const refreshBalanceSheetList = () => {
    const params: any = {
      page: 1,
      per_page: 5000,
      year: searchParams.year['value'],
      month: searchParams.month['value'],
      branch_id: +branchData['id'],
    }

    setIsLoading(true)
    balanceSheetRefresh(
      params,
      (message: string, resp: any) => {
        setIsLoading(false)
        if (
          resp &&
          resp['data'] &&
          resp['data']['data'] &&
          resp['data']['data']['result']
        ) {
          setGlBalanceSheetList(resp.data.data.result)
        } else {
          setGlBalanceSheetList([])
        }

        if (
          resp &&
          resp['data'] &&
          resp['data']['data'] &&
          resp['data']['data']
        ) {
          setIsNotify(resp.data.data?.is_notify || false)
        }
      },
      (message: string, resp: any) => {
        setGlBalanceSheetList([])
        setIsLoading(false)
        toast.error(message)
      }
    )
  }

  const _downloadReport = async () => {
    if (!contentRef.current) {
      return
    }

    try {
      setIsDownloading(true)
       await html2pdf()
            .from(contentRef.current)
            .set({
                margin: [20, 10, 10, 10],
              filename: `balance-sheet-${moment().format('YYYY-MM-DD')}.pdf`,
              image: { type: "jpeg", quality: 0.5 },
              html2canvas: {
                scale: 2,
                useCORS: true,
              },
              jsPDF: {
                unit: "mm",
                format: "a4",
                orientation: "landscape",
              },
                         pagebreak: {
                mode: ['avoid-all', 'css', 'legacy'],
                before: '.page-break-before',
                after: '.page-break-after',
                avoid: '.no-page-break'
            }
            })

            .save();


      setIsDownloading(false)
    } catch (err) {
      console.error(err)
      setIsDownloading(false)
    }



  }

  const _handleChangeSearchParam = (e: any, type: string) => {
    // Clear any prior data-issue state when switching the viewed period.
    setDataIssueAfterRegen(false)
    // Drop the rows for the period being navigated away from. The list is only
    // refetched when Generate is pressed, so leaving them up renders one month's
    // figures under another month's heading - Jan 2026 showing Jan 2024's balance
    // sheet, with no indication the data is stale.
    setGlBalanceSheetList([])
    setRegenerationPending(null)
    if (type === 'month') {
      setSearchParams({
        ...searchParams,
        month: e,
      })
    } else if (type === 'year') {
      setSearchParams({
        ...searchParams,
        year: e,
      })
    }
  }

  const getSettingsData = (onDone?: () => void) => {
    fetchAccountSettingsData(
      { branch_id: branchId },
      (msg: string, resp: any) => {
        onDone?.()
      },
      (msg: string, resp: any) => {
        onDone?.()
      }
    )
  }

  const withSettingsCheck = async (action: () => void) => {
    try {
      await new Promise<any>((resolve, reject) => {
        fetchAccountSettingsData(
          { branch_id: branchId },
          (_: string, resp: any) => resolve(resp),
          (_: string, resp: any) => reject(resp)
        )
      })
      if (isRefreshInProgress) return
      action()
    } catch (err) {
       toast.error(t('alerts.failCheckSettings'))
    }
  }

  const getLastDayOfMonth = (month: any, year: any) => {
    return new Date(year, month, 0).getDate()
  }

  const _handleShowViewRegister = (data: any, hasEditAccess: boolean) => {
    setTxnRegisterPayload({
      startDate: `${searchParams.year['value']}-${searchParams.month['value']}-01`,
      endDate: `${searchParams.year['value']}-${
        searchParams.month['value']
      }-${getLastDayOfMonth(
        searchParams.month['value'],
        searchParams.year['value']
      )}`,
      account: data['account_chart']['id'],
      hasEditAccess: isThirdPartyEnabled ? false : hasEditAccess,
    })
    setIsShowViewRegister(true)
  }

  const _handleCloseViewRegister = () => {
    setIsShowViewRegister(false)
    // Editing/adding transactions in the register changes the underlying data and
    // triggers a fresh auto-cascade, so any prior "data issue" verdict is now stale.
    setDataIssueAfterRegen(false)
    fetchBalanceSheetList()
  }

  useEffect(() => {
    if (!isRefreshInProgress) return
    const intervalId = setInterval(() => {
      getSettingsData()
    }, 5000)
    return () => clearInterval(intervalId)
  }, [isRefreshInProgress, branchId])

  useEffect(() => {
    const wasRefreshInProgress = previousRefreshInProgressRef.current

    if (wasRefreshInProgress && !isRefreshInProgress && branchId) {
      showRefreshCompletedToast()
      // Re-fetch so the imbalance banner re-evaluates against the freshly
      // regenerated figures (clears if balanced, persists if still off).
      fetchBalanceSheetList()
    }

    previousRefreshInProgressRef.current = isRefreshInProgress
  }, [isRefreshInProgress, branchId])

  return (
    <div className="main-container flex-grow-1" >
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center flex-wrap pb-3 pt-2">
            <div>
              <h1 className="h5 font-weight-700 mb-0 d-inline-flex text-black">
                {t('title')}
              </h1>
            </div>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <div className="filter-box">
                  <Select
                    onChange={(e: any) =>
                      _handleChangeSearchParam(e, 'month')
                    }
                    options={MONTHS}
                    defaultValue={searchParams['month']}
                    className={'zindex-10'}
                  />
                </div>
                <div className="filter-box">
                  <Select
                    onChange={(e: any) =>
                      _handleChangeSearchParam(e, 'year')
                    }
                    options={lastYears(5)}
                    defaultValue={searchParams['year']}
                    className={'zindex-10'}
                  />
                </div>

                <div className="filter-box">
                  <button
                    type="button"
                    onClick={fetchBalanceSheetList}
                    className="btn-primary btn btn-lg"
                  >
                    {t('filter.generate_report')} &nbsp;{' '}
                    <i className="fa fa-search" aria-hidden="true"></i>
                  </button>{' '}
                  &nbsp;
                  {!isThirdPartyEnabled && glBalanceSheetList.length ? (
                    <button
                      type="button"
                      onClick={refreshBalanceSheetList}
                      className="btn-warning btn btn-lg d-none"
                    >
                      {t('filter.refresh')} &nbsp;{' '}
                      <i className="fa fa-refresh" aria-hidden="true"></i>
                    </button>
                  ) : (
                    <></>
                  )}
                </div>
                {glBalanceSheetList.length ? (
                  <div className="filter-box text-end">
                    <button
                      type="button"
                      onClick={_downloadReport}
                      className="btn-dark btn btn-lg"
                    >
                      {isDownloading ? (
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                      ) : (
                        <div>
                          {t('filter.export_pdf')} &nbsp;
                          <i
                            className="fa fa-download"
                            aria-hidden="true"
                          ></i>
                        </div>
                      )}
                    </button>
                  </div>
                ) : (
                  <></>
                )}

                <div className="filter-btn">
                  <div
                    className={`page-loader ${
                      isLoading ? 'visible' : 'hidden'
                    }`}
                  >
                    <div className="loader"></div>
                  </div>
                </div>
              </div>
          </div>
        {
          // !isThirdPartyEnabled && isNotify ? (
          //     <Alert variant={'info'}>
          //         <i className="fa fa-exclamation-triangle fa-lg" aria-hidden="true"></i>&nbsp;&nbsp;
          //         Please refresh the balance sheet to reflect new updates.
          //     </Alert>
          // ) : <></>
        }
        {showRefreshBanner && (
          <ReportRefreshBanner
            open={showRefreshBanner}
            total={accountSettings.bs_refresh_total}
            completed={accountSettings.bs_refresh_completed}
            startedAt={accountSettings.bs_refresh_started_at}
          />
        )}
        {showRegenerationFailedBanner && (
          <Snackbar
            open={true}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            autoHideDuration={null}
          >
            <Alert variant={'danger'} className="d-flex align-items-center justify-content-between p-2">
              <div>
                <i className="fa fa-exclamation-triangle fa-lg" aria-hidden="true"></i>&nbsp;&nbsp;
                <strong>Report regeneration stopped</strong>{' '}
                {bsRegenerationFailedMonth
                  ? `Balance sheet regeneration stopped at ${new Date(
                      +bsRegenerationFailedMonth.split('-')[0],
                      +bsRegenerationFailedMonth.split('-')[1] - 1
                    ).toLocaleString('en-US', { month: 'long', year: 'numeric' })} after repeated failures -- some months may show outdated data.`
                  : 'Balance sheet regeneration stopped after repeated failures -- some months may show outdated data.'}
                {bsRegenerationFailedMsg && (
                  <div style={{ fontSize: '0.85em', marginTop: 4, opacity: 0.8 }}>
                    {bsRegenerationFailedMsg}
                  </div>
                )}
              </div>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={handleRetryFailedRegeneration}
                style={{ marginLeft: '10px' }}
              >
                <i className="fa fa-refresh" aria-hidden="true"></i>&nbsp;
                Retry
              </button>
            </Alert>
          </Snackbar>
        )}
        {!isBalanced && glBalanceSheetList.length > 0 && (
          dataIssueAfterRegen ? (
            <Snackbar
              open={true}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              autoHideDuration={null}
              sx={{
                width: showRefreshBanner ? '40%' : '100%',
              }}
            >
              <Alert variant={'danger'} className="d-flex align-items-center p-2">
                <div>
                  <i className="fa fa-exclamation-triangle fa-lg" aria-hidden="true"></i>&nbsp;&nbsp;
                    {t('alerts.imbalanceStillTitle', {
                    difference: balanceDifference.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }),
                  })}
                  {firstImbalancedMonth && firstImbalancedYear && (
                    <>
                      {' '}
                      <Link
                        to={`${URLS.VIEW_REGISTER}?month=${firstImbalancedMonth}&year=${firstImbalancedYear}`}
                        style={{ color: 'inherit', fontWeight: 'bold', textDecoration: 'underline' }}
                      >
                        {t('alerts.reviewTransactionsLink', {
                          date: new Date(firstImbalancedYear, firstImbalancedMonth - 1).toLocaleString('en-US', {
                            month: 'long',
                            year: 'numeric',
                          }),
                        })}
                      </Link>
                    </>
                  )}
                </div>
              </Alert>
            </Snackbar>
          ) : (
            <Snackbar
              open={true}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              autoHideDuration={null}
                sx={{
                width: showRefreshBanner ? '40%' : '100%',
              }}
              // onClose={() => setShowSnackbar(false)}
            >
              <Alert variant={'warning'} className="d-flex align-items-center justify-content-between p-2">
                <div>
                  <i className="fa fa-exclamation-triangle fa-lg" aria-hidden="true"></i>&nbsp;&nbsp;
                  <strong>{t('alerts.imbalanceWarningTitle')}</strong>{' '}
                  {t('alerts.imbalanceWarningText', {
                    difference: balanceDifference.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }),
                  })}
                  {firstImbalancedMonth && firstImbalancedYear && (
                    <span>
                      {' '}
                      {t('alerts.imbalanceOccurrenceNote', {
                        date: new Date(firstImbalancedYear, firstImbalancedMonth - 1).toLocaleString('en-US', {
                          month: 'long',
                          year: 'numeric',
                        }),
                      })}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className="btn btn-warning btn-sm "
                  onClick={() => setShowConfirm(true)}
                  style={{ marginLeft: '10px' }}
                >
                  <i className="fa fa-refresh" aria-hidden="true"></i>&nbsp;
                  {t('labels.regenerate')}
                </button>
              </Alert>
            </Snackbar>
          )
        )}
        {unreconciledPeriods.length > 0 && glBalanceSheetList.length > 0 && (
          <Alert variant={'warning'} className="d-flex align-items-center justify-content-between p-2">
            <div>
              <i className="fa fa-exclamation-triangle fa-lg" aria-hidden="true"></i>&nbsp;&nbsp;
              <strong>{t('alerts.unreconciledPeriodsTitle')}</strong>{' '}
              {t('alerts.unreconciledPeriodsText', { count: unreconciledPeriods.length })}
              <ul className="mb-0 mt-1">
                {Object.values(
                  unreconciledPeriods.reduce((acc: Record<string, { name: string; dates: string[] }>, item: any) => {
                    const key = String(item.bank?.id)
                    if (!acc[key]) {
                      acc[key] = {
                        name: `${item.bank?.account ?? ''} ${item.bank?.name ?? ''}`.trim(),
                        dates: [],
                      }
                    }
                    acc[key].dates.push(
                      new Date(item.period.year, item.period.month - 1).toLocaleString('en-US', {
                        month: 'short',
                        year: 'numeric',
                      })
                    )
                    return acc
                  }, {})
                ).map((bank) => (
                  <li key={bank.name}>
                    <strong>{bank.name}:</strong> {bank.dates.reverse().join(', ')}
                  </li>
                ))}
              </ul>
            </div>
            <Link to={URLS.BANK_RECONCILE} className="btn btn-warning btn-sm" style={{ marginLeft: '10px' }}>
              {t('alerts.unreconciledPeriodsLink')}
            </Link>
          </Alert>
        )}
        <div className="executive-dashboard table-design">
          {glBalanceSheetList.length ? (
            <div className="row">
              <div className="col-md-12"
                style={{
                  padding: '6px',
                  backgroundColor: '#00205c',
                  flexDirection: 'row',
                  display: 'flex',
                }}
              >
                <div className="col-md-4">
                  <span></span>
                </div>
                <div className="col-md-4 text-center">
                  {/* <h2>Month ending</h2> */}
                  <span className="fs-14 text-white">{t('table.header.current_month')}(  {moment(
                      `${+searchParams['year']['value']}-${
                        searchParams['month']['value']
                      }-01`
                    )
                      .endOf('month')
                      .format('MM/DD/YYYY')})</span>
                </div>
                <div className="col-md-4 text-center">
                  {/* <h2>Prior Year Month ending</h2> */}
                  <span className="fs-14 h5 text-white">{t('table.header.prior_year')}( {moment(
                      `${+searchParams['year']['value'] - 1}-${
                        searchParams['month']['value']
                      }-01`
                    )
                      .endOf('month')
                      .format('MM/DD/YYYY')})</span>
                </div>
              </div>
                    <div
                className="balance-sheet-table-body col-md-12"
              style={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' }}
                ref={tableContainerRef}
              >
                {glBalanceSheetList.map((data, index) => {
                  const { report_code } = data['account_chart']
                  const shouldHideRow =
                    ['VP', 'VP/IP', 'IP'].includes(report_code) &&
                    !data['prev_year_total_amount'] &&
                    !data['total_amount']
                      ? 'd-none'
                      : ''
                  if (shouldHideRow == '') {
                    return (
                      <div
                        className={`bs-table-body-row bs-table-body-${data['account_chart']['report_code']} ${shouldHideRow} resize-font`}
                        key={index}
                      >
                        <div className="col-md-4">
                          <h4
                            style={
                              ['VP', 'VP/IP', 'IP'].includes(
                                data['account_chart']['report_code']
                              )
                                ? {
                                    marginLeft: '35px',
                                    whiteSpace: 'normal',
                                    wordWrap: 'break-word',
                                    overflowWrap: 'break-word',
                                  }
                                : {
                                    whiteSpace: 'normal',
                                    wordWrap: 'break-word',
                                    overflowWrap: 'break-word',
                                  }
                            }
                          >
                            {data['account_chart']['name']}
                          </h4>
                        </div>
                        {(!['GH', 'SH', 'SSH'].includes(
                          data['account_chart']['report_code']
                        ) && (
                          <>
                            <div className="col-md-4 d-flex">
                              <div className="col-md-3 text-end">
                                {['VP', 'VP/IP', 'IP'].includes(
                                  data['account_chart']['report_code']
                                ) &&
                                  (data['total_amount'] && data['total_amount'] == 0 ||
                                  data['account_chart']['report_code'] !=
                                    'VP' ? (
                                    <h4 style={{ whiteSpace: 'nowrap' }}>
                                      {data['total_amount'] && data['total_amount'] >= 0
                                        ? usdCurrencyFormat(
                                            data['total_amount'] || 0
                                          )
                                        : `(${usdCurrencyFormat(
                                            Math.abs(data['total_amount'] || 0)
                                          )})`}
                                    </h4>
                                  ) : (
                                    <h4
                                      style={{ whiteSpace: 'nowrap' }}
                                      onClick={() =>
                                        _handleShowViewRegister(data, true)
                                      }
                                      className="clickable_link"
                                    >
                                      {data['total_amount'] && data['total_amount'] > 0
                                        ? usdCurrencyFormat(
                                            data['total_amount']
                                          )
                                        : `(${usdCurrencyFormat(
                                            Math.abs(data['total_amount']!!)
                                          )})`}
                                    </h4>
                                  ))}
                              </div>
                              <div className="col-md-3 text-end">
                                {data['account_chart']['report_code'] ===
                                  'SST' && (
                                  <h4
                                    className={
                                      data['account_chart']['report_code'] +
                                      '-sum'
                                    }
                                  >
                                    {data['total_amount'] && data['total_amount'] >= 0
                                      ? usdCurrencyFormat(
                                          data['total_amount'] || 0
                                        )
                                      : `(${usdCurrencyFormat(
                                          Math.abs(data['total_amount'] || 0)
                                        )})`}
                                  </h4>
                                )}
                              </div>
                              <div className="col-md-3 text-end">
                                {data['account_chart']['report_code'] ===
                                  'ST' && (
                                  <h4
                                    className={
                                      data['account_chart']['report_code'] +
                                      '-sum'
                                    }
                                  >
                                    {data['total_amount'] &&  data['total_amount'] >= 0
                                      ? usdCurrencyFormat(
                                          data['total_amount'] || 0
                                        )
                                      : `(${usdCurrencyFormat(
                                          Math.abs(data['total_amount'] || 0)
                                        )})`}
                                  </h4>
                                )}
                              </div>
                              <div className="col-md-3 text-end">
                                {data['account_chart']['report_code'] ===
                                  'GT' && (
                                  <h4
                                    className={
                                      data['account_chart']['report_code'] +
                                      '-sum'
                                    }
                                  >
                                    {data['total_amount'] && data['total_amount'] >= 0
                                      ? usdCurrencyFormat(
                                          data['total_amount'] || 0
                                        )
                                      : `(${usdCurrencyFormat(
                                          Math.abs(data['total_amount'] || 0)
                                        )})`}
                                  </h4>
                                )}
                              </div>
                            </div>
                            <div className="col-md-4 d-flex">
                              <div className="col-md-3 text-end">
                                {['VP', 'VP/IP', 'IP'].includes(
                                  data['account_chart']['report_code']
                                ) && (
                                  <h4>
                                    {data['prev_year_total_amount'] && data['prev_year_total_amount'] >= 0
                                      ? usdCurrencyFormat(
                                          data['prev_year_total_amount'] ||
                                            0
                                        )
                                      : `(${usdCurrencyFormat(
                                          Math.abs(
                                            data['prev_year_total_amount'] || 0
                                          )
                                        )})`}
                                  </h4>
                                )}
                              </div>
                              <div className="col-md-3 text-end">
                                {data['account_chart']['report_code'] ===
                                  'SST' && (
                                  <h4
                                    className={
                                      data['account_chart']['report_code'] +
                                      '-sum'
                                    }
                                  >
                                    {data['prev_year_total_amount'] && data['prev_year_total_amount'] >= 0
                                      ? usdCurrencyFormat(
                                          data['prev_year_total_amount'] ||
                                            0
                                        )
                                      : `(${usdCurrencyFormat(
                                          Math.abs(
                                            data['prev_year_total_amount'] || 0
                                          )
                                        )})`}
                                  </h4>
                                )}
                              </div>
                              <div className="col-md-3 text-end">
                                {data['account_chart']['report_code'] ===
                                  'ST' && (
                                  <h4
                                    className={
                                      data['account_chart']['report_code'] +
                                      '-sum'
                                    }
                                  >
                                    {data['prev_year_total_amount'] && data['prev_year_total_amount']>= 0
                                      ? usdCurrencyFormat(
                                          data['prev_year_total_amount'] ||
                                            0
                                        )
                                      : `(${usdCurrencyFormat(
                                          Math.abs(
                                            data['prev_year_total_amount'] || 0
                                          )
                                        )})`}
                                  </h4>
                                )}
                              </div>
                              <div className="col-md-3 text-end">
                                {data['account_chart']['report_code'] ===
                                  'GT' && (
                                  <h4
                                    className={
                                      data['account_chart']['report_code'] +
                                      '-sum'
                                    }
                                  >
                                    {data['prev_year_total_amount'] && data['prev_year_total_amount'] >= 0
                                      ? usdCurrencyFormat(
                                          data['prev_year_total_amount'] ||
                                            0
                                        )
                                      : `(${usdCurrencyFormat(
                                          Math.abs(
                                            data['prev_year_total_amount'] || 0
                                          )
                                        )})`}
                                  </h4>
                                )}
                              </div>
                            </div>
                          </>
                        )) || (
                          <>
                            <div className="col-md-4 d-flex">
                              <div className="col-md-3">
                                <h4></h4>
                              </div>
                              <div className="col-md-3">
                                <h4></h4>
                              </div>
                              <div className="col-md-3">
                                <h4></h4>
                              </div>
                              <div className="col-md-3">
                                <h4></h4>
                              </div>
                            </div>
                            <div className="col-md-4 d-flex">
                              <div className="col-md-3">
                                <h4></h4>
                              </div>
                              <div className="col-md-3">
                                <h4></h4>
                              </div>
                              <div className="col-md-3">
                                <h4></h4>
                              </div>
                              <div className="col-md-3">
                                <h4></h4>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    )
                  }
                })}
              </div>
            </div>
          ) : (
            <div className="text-center">
              <span>
                {regenerationPending
                  ? t('table.regenerating', {
                      completed: regenerationPending?.params?.completed ?? 0,
                      total: regenerationPending?.params?.total ?? 0,
                      pending: regenerationPending?.params?.pending ?? 0,
                      context: regenerationPending?.params?.total ? 'progress' : 'queued',
                    })
                  : t('table.empty')}
              </span>
            </div>
          )}
        </div>
        <ScrollToggle scrollRef={tableContainerRef} data={glBalanceSheetList} />
      </div>
        <div
				style={{
					position: "fixed",
					top: "-10000px",
					left: "-10000px",
				}}
			>
				{glBalanceSheetList &&
            <GenerateBS data={glBalanceSheetList} companyName={companyInfo?.business_name ?? ''} currentDate={currentDate} priorDate={priorDate} ref={contentRef} />
				}
			</div>

      <SAlert
        show={showConfirm}
        icon={'warning'}
        showCancel={true}
        confirmBtnText={t('alert.regenerate.confirm')}
        cancelBtnText={t('alert.regenerate.cancel')}
        confirmBtnBsStyle={'danger'}
        cancelBtnBsStyle={'secondary'}
        title={t('alert.regenerate.title')}
        msg={t('alert.regenerate.msg')}
        onConfirm={handleRegenerate}
        onCancel={() => setShowConfirm(false)}
      />

      {isShowViewRegister ? (
        <ViewRegisterModal
          show={isShowViewRegister}
          onHide={() => _handleCloseViewRegister()}
          startDate={txnRegisterPayload.startDate}
          endDate={txnRegisterPayload.endDate}
          account={txnRegisterPayload.account}
          hasEditAccess={txnRegisterPayload.hasEditAccess}
          header={t('modal.header', {
            monthYear: moment(
              searchParams.year['value'] +
                '-' +
                searchParams.month['value'] +
                '-01'
            ).format('MMM, YYYY'),
          })}
          emptyMsg={t('modal.emptyMsg')}
          showBeginningAndEndingBalance={true}
        />
      ) : (
        <></>
      )}
    </div>
  )
}

export default BalanceSheet
